import { TFunction } from 'i18next';

import { AttributeType } from 'companySettings/enums';
import { IPartFinderAttributeValue } from 'companySettings/types';

interface IRangeInput {
  increment: string;
  max: string;
  min: string;
  unit: string;
}

// A range needs a max other than its min; a max equal to the min is a single number too.
export const isRangeInput = (min: string, max: string) =>
  Boolean(max) && Number(max) !== Number(min);

// The value an entry of the Number & Range editor makes: a single number (which has no increment)
// or a range, each with its unit when one was typed.
export const buildRangeValue = ({
  min,
  max,
  increment,
  unit,
}: IRangeInput): IPartFinderAttributeValue => {
  const isRange = isRangeInput(min, max);

  return {
    rangeMin: min,
    ...(isRange && { rangeMax: max }),
    ...(isRange && increment && { rangeIncrement: increment }),
    ...(unit && { rangeUnit: unit }),
  };
};

// How a value reads everywhere: its text or colour name, a number like "10 ft", or a range like
// "0-125 ft (Increment 5)".
export const getAttributeValueLabel = (
  type: AttributeType,
  value: IPartFinderAttributeValue,
  t: TFunction
) => {
  if (AttributeType.NUMBER_RANGE !== type) {
    return value?.value ?? '';
  }

  // A spaced dash keeps a negative bound readable (-40 – -10) and keeps the CSV cell from reading
  // as a formula.
  const numbers = value?.rangeMax ? `${value.rangeMin} – ${value.rangeMax}` : `${value?.rangeMin}`;
  const range = [numbers, value?.rangeUnit].filter(Boolean).join(' ');

  return value?.rangeIncrement
    ? `${range} ${t('labelIncrementValue', { increment: value.rangeIncrement })}`
    : range;
};

// Two values with the same key are duplicates: labels compare case-insensitively, numbers and
// ranges by their numbers (so 5 and 5.0 match), increment and unit. A single number has no max.
export const getAttributeValueKey = (type: AttributeType, value: IPartFinderAttributeValue) => {
  if (AttributeType.NUMBER_RANGE === type) {
    return [
      Number(value?.rangeMin),
      value?.rangeMax ? Number(value.rangeMax) : '',
      value?.rangeIncrement ? Number(value.rangeIncrement) : '',
      (value?.rangeUnit ?? '').trim().toLowerCase(),
    ].join('|');
  }

  return (value?.value ?? '').trim().toLowerCase();
};
